// This module exports `contents`, a promise, with an extra method `contents.fetch(queryroot)`
// which fetches from the server and eventually resolves/rejects the promise.


// FORMAT EXPECTED FROM THE SERVER:
// An html document with sections and questions.
// (The first part <h1>title</h1>... becomes both a section and a question.)
//
// <h1 id="label/" data-imports="i1 i2">title</h2>
// ...
//
// <h2 id="label/" data-imports="i1 i2">title</h2>
//
// <article id="label" data-imports="i1 i2">
//   <h1>title</h1>
//   contents
//   <pre class="prompt">...</pre>
//   <pre class="answer">...</pre>
// </article>

// FORMAT NEEDED BY THE FRONT END:
// contents: {queryroot, rootQuestion, sections:[section,...]}
// section: {label, titles[str,...], questions:[question,...]}
// question: {label, title, html, source, answer, imports, eval, 
//            slug, contents, section, next, prev}




let resolveContents, rejectContents;

export const contents = new Promise(function(resolve,reject) {
    resolveContents = resolve;
    rejectContents = reject;
});


async function fetchContents(queryroot, errorCallback) {
    function err(e) {
        if (errorCallback)
            errorCallback(e);
        else
            console.log("Error fetching contents:", e);
        rejectContents(e);
    }

    if (queryroot[queryroot.length - 1] != '/') queryroot = queryroot + '/';
    const endpoint = `${queryroot.substr(0,queryroot.length-1)}_q.html`;
    const response = await fetch(endpoint);
    if (!response.ok) { 
        err(response.statusText); 
        return; 
    }
    const data = await response.text();
    const { questions, titles } = parseHtmlQuestions(data);
    if (questions == null || titles == null) { err("Unexpected response from server"); return; }
    try {
        const contents = constructContents({queryroot, questions, titles});
        resolveContents(contents);
    } catch (e) {
        err("Unexpected response format from server");
    }
}

 contents.fetch = function(queryroot, errorCallback) {
     fetchContents(queryroot, errorCallback);
 }   
    

//-------------------------------------------------------------

function parseHtmlQuestions(txt) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(txt, 'text/html');
    const questions = [...doc.getElementsByTagName("article")].map(x => parseHtmlQuestion(x));
    const titles = [...doc.querySelectorAll("body > h2")].map(x => parseHtmlTitle(x));
    const {question:q0,title:t0} = parseHtmlQuestion0(doc.body);
    return {questions: [q0, ...questions], titles: [t0, ...titles]};

}

function parseHtmlQuestion(x) {
    // INPUT: <article id="label" data-imports="i1 i2"><h1>title</h1> contents <pre class="prompt">q</pre> <pre class="answer">q</pre></article>
    // OUTPUT: {label, description:{title, html, source, imports}, answer}
    const label = x.id;
    var title, source, answer;
    const html = document.createElement("div");
    for (const y of x.children) {
        if (y.tagName == 'H1' & typeof title == 'undefined') title = y.textContent;
        else if (y.tagName == 'PRE' & y.classList.contains('prompt')) source = y.textContent.trimEnd();
        else if (y.tagName == 'PRE' & y.classList.contains('answer')) answer = y.textContent.trimEnd();
        else html.appendChild(y.cloneNode(true));
    }
    const imports = x.dataset?.imports?.split(" ");
    return {label, description: {title, html: html.innerHTML, source, imports}, answer};
}

function parseHtmlTitle(x) {
    // INPUT: <h2 id="label" data-imports="i1 i2">title</h2>
    // OUTPUT: {label, description:{title, imports:[i1,i2]}}
    return {label: x.id, description:{title: x.textContent, imports: x.dataset.imports?.split(" ")}};
}

function parseHtmlQuestion0(x) {
    // INPUT: <h1 id="label/" data-imports="i1 i2">title</h1> html <article ...
    // OUTPUT: {question: {label, description: {title, html}}, section: {label/, desciption:{title, imports}}}
    var h1;
    const html = document.createElement("div");
    for (const y of x.children) {
        if (y.tagName == 'H1' & typeof h1 == 'undefined') h1 = y;
        else if (y.tagName == 'ARTICLE') break;
        else if (y.tagName == 'H2') break;
        else html.appendChild(y.cloneNode(true));
    }
    const label = h1.id[h1.id.length - 1] == '/' ? h1.id.substr(0, h1.id.length - 1) : h1.id;
    const imports = h1.dataset.imports?.split(" ");
    return {
        question: {label, description: {title:h1.textContent, html:html.innerHTML}},
        title: {label:label+'/', description: {title:h1.textContent, imports}}
    }
}


function Section(d) {
    return {...d, subsections:[], questions:[]};
}

function addSecToTree(sec, tree) {
    if (sec.label.indexOf(tree.label) != 0) {
        console.log(`No place for section ${sec.label} in tree ${tree.label}`);
        return tree;
    }
    if (sec.label == tree.label) {
        sec.subsections = tree.subsections;
        return sec;
    }
    const i = tree.subsections.findIndex(b => sec.label.indexOf(b.label) == 0); // b.q is a prefix for sec.q
    if (i >= 0) {
        tree.subsections[i] = addSecToTree(sec, tree.subsections[i]);
        return tree;
    }
    const subsec = tree.subsections.filter(b => b.label.indexOf(sec.label) == 0); // sec.q is a prefix for b.q;
    const b = tree.subsections.filter(b => b.label.indexOf(sec.label) != 0);
    sec.subsections = [...sec.subsections, ...subsec];
    b.push(sec);
    tree.subsections = b;
    return tree;
}

function addQuestionToTree(question, tree, imports) {
    imports = [...(imports ?? []), ...(tree.description.imports ?? [])];
    const i = tree.subsections.findIndex(b => question.label.indexOf(b.label) == 0); // b.q is a prefix for q
    if (i >= 0)
        tree.subsections[i] = addQuestionToTree(question, tree.subsections[i], imports);
    else if (question.label.indexOf(tree.label) == 0) {
        question.imports = [...imports, ...(question.imports ?? [])];
        tree.questions.push(question);
    }
    else
        console.log(`No place for question ${question.label} in tree ${tree.label}`);
    return tree;
}

function constructContents({queryroot, questions, titles}) {
    let root = Section({label:queryroot, title:null});
    let rootQuestion;

    // Turn these objects from the server-api format to what I like
    // Also, for any questions with href="#/queryroot/slug", replace with href="#/slug"
    const m = new RegExp(`href=(['"])#/${queryroot}`, "g");
    const r = `href=$1#/`;
    questions = questions.map(q => ({...q, ...q.description, html: q.description.html.replace(m,r)}));
    

    // Add sections and questions to a tree
    for (const sec of titles) 
        root = addSecToTree(Section(sec), root);
    for (const question of questions) {
        if (question.label + '/' == root.label)
            rootQuestion = question;
        else
            root = addQuestionToTree(question, root);
    }

    // Flatten the tree
    const flatSections = [];
    function visit(secPath) {
        const sec = secPath[secPath.length-1];
        if (sec.questions.length > 0)
            flatSections.push({label:sec.label, titles:secPath.slice(1).map(s => s.description.title), questions:sec.questions});
        for (const subsec of sec.subsections) 
            visit([...secPath, subsec]);
    }
    visit([root]);

    // Give each question a pointer to prev and next question, and to the full contents
    let contents = {
        queryroot,
        title: root, 
        rootQuestion, 
        sections:flatSections
    };

    let lastq = null;
    for (const sec of flatSections) for (const question of sec.questions) {
        if (lastq != null) {
            lastq.next = question;
            question.prev = lastq;
        }
        lastq = question;
        question.contents = contents;
        question.section = sec;
        question.slug = question.label.substring(queryroot.length);
    }

    return contents;
}

